/**
 * ProductTourServicesList Component
 * 
 * Dahil/dahil değil hizmetler listesi (reusable)
 */

'use client';

import { motion } from 'framer-motion';
import { cardVariants, listItemVariants } from '@/lib/animations';
import { Service } from '@/services/ProductTourDataService';

export interface ProductTourServicesListProps {
    included: Service[];
    excluded: Service[];
    language?: string;
    includedTitle?: string; // ProductTypeField'dan gelecek başlık
    excludedTitle?: string; // ProductTypeField'dan gelecek başlık
}

export default function ProductTourServicesList({
    included,
    excluded,
    language = 'en',
    includedTitle,
    excludedTitle,
}: ProductTourServicesListProps) {
    // Önce ProductTypeField'dan gelen başlıkları kullan, yoksa fallback
    const translations: Record<string, { included: string; excluded: string }> = {
        en: { included: 'Included/Package', excluded: 'Excluded/Package' },
        tr: { included: 'Dahil/Paket', excluded: 'Dahil Değil/Paket' },
    };

    const fallbackT = translations[language] || translations.en;
    const t = {
        included: includedTitle || fallbackT.included,
        excluded: excludedTitle || fallbackT.excluded,
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {included.length > 0 && (
                <motion.div variants={cardVariants}>
                    <h4 className="font-semibold text-alsero-dark-gray mb-4">{t.included}</h4>
                    <ul className="space-y-2">
                        {included
                            .filter(service => {
                                if (!service || typeof service !== 'object') return false;
                                const title = service.title || service.name || service.value || service.text || '';
                                return typeof title === 'string' && title.trim().length > 0;
                            })
                            .map((service, index) => {
                                const title = service.title || service.name || service.value || service.text || '';
                                const description = service.description || service.desc || service.detail || '';
                                return (
                                    <motion.li
                                        key={index}
                                        className="flex items-center gap-2"
                                        variants={listItemVariants}
                                    >
                                        <svg
                                            className="w-5 h-5 text-green-500 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <span className="text-alsero-light-gray">{String(title).trim()}</span>
                                            {description && String(description).trim() && (
                                                <p className="text-alsero-light-gray text-xs mt-1">
                                                    {String(description).trim()}
                                                </p>
                                            )}
                                        </div>
                                    </motion.li>
                                );
                            })}
                    </ul>
                </motion.div>
            )}

            {excluded.length > 0 && (
                <motion.div variants={cardVariants}>
                    <h4 className="font-semibold text-alsero-dark-gray mb-4">{t.excluded}</h4>
                    <ul className="space-y-2">
                        {excluded
                            .filter(service => {
                                if (!service || typeof service !== 'object') return false;
                                const title = service.title || service.name || service.value || service.text || '';
                                return typeof title === 'string' && title.trim().length > 0;
                            })
                            .map((service, index) => {
                                const title = service.title || service.name || service.value || service.text || '';
                                const description = service.description || service.desc || service.detail || '';
                                return (
                                    <motion.li
                                        key={index}
                                        className="flex items-center gap-2"
                                        variants={listItemVariants}
                                    >
                                        <svg
                                            className="w-5 h-5 text-red-500 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <div className="flex-1">
                                            <span className="text-alsero-light-gray">{String(title).trim()}</span>
                                            {description && String(description).trim() && (
                                                <p className="text-alsero-light-gray text-xs mt-1">
                                                    {String(description).trim()}
                                                </p>
                                            )}
                                        </div>
                                    </motion.li>
                                );
                            })}
                    </ul>
                </motion.div>
            )}
        </div>
    );
}

